// src/components/goals/GoalsTable.tsx
import React from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { GoalData, ColumnKey } from '../../pages/Goals'; // Adjust path

interface GoalsTableProps {
  paginatedData: GoalData[];
  visibleColumns: ColumnKey[];
  getColumnDisplayName: (col: ColumnKey) => string;
  getSortIndicator: (key: ColumnKey) => string;
  handleSort: (key: ColumnKey) => void;
  columnFilters: Partial<Record<ColumnKey, string>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<Partial<Record<ColumnKey, string>>>>;
  selectedRows: string[];
  handleSelectAll: () => void;
  handleRowSelect: (id: string) => void;
  handleEdit: (goal: GoalData) => void;
  handleDelete: (id: string) => void;
  formatCurrency: (value: string | number) => string;
  getLabel: (group: string, value: string) => string;
  lookupGroupMap: Record<string, string>;
}

const GoalsTable: React.FC<GoalsTableProps> = (props) => {
  const {
    paginatedData, visibleColumns, getColumnDisplayName, getSortIndicator, handleSort,
    columnFilters, setColumnFilters, selectedRows, handleSelectAll, handleRowSelect,
    handleEdit, handleDelete, formatCurrency, getLabel, lookupGroupMap
  } = props;

  return (
    <div className="overflow-x-auto">
      <table className="w-full divide-y divide-gray-200 min-w-max">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
              <input type="checkbox" checked={selectedRows.length === paginatedData.length && paginatedData.length > 0} onChange={handleSelectAll} className="rounded text-purple-600 focus:ring-purple-600" />
            </th>
            {visibleColumns.map(col => (
              <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider cursor-pointer hover:bg-gray-100">
                <div className="flex items-center justify-between" onClick={() => handleSort(col)}>
                  <span>{getColumnDisplayName(col)}</span>
                  <span className="text-xs text-gray-400">{getSortIndicator(col)}</span>
                </div>
                <input
                  type="text"
                  placeholder={`Filter...`}
                  value={columnFilters[col] || ''}
                  onClick={e => e.stopPropagation()}
                  onChange={e => setColumnFilters(prev => ({ ...prev, [col]: e.target.value }))}
                  className="mt-1 w-full p-1 border border-gray-300 rounded-lg text-xs"
                />
              </th>
            ))}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {paginatedData.map(goal => (
            <tr key={goal.id} className={`hover:bg-gray-50 ${selectedRows.includes(goal.id) ? 'bg-purple-50' : ''}`}>
              <td className="px-6 py-4 whitespace-nowrap">
                <input type="checkbox" checked={selectedRows.includes(goal.id)} onChange={() => handleRowSelect(goal.id)} className="rounded text-purple-600 focus:ring-purple-600" />
              </td>
              {visibleColumns.map(col => (
                <td key={col} className="px-6 py-4 whitespace-nowrap text-sm">
                  {lookupGroupMap[col]
                    ? getLabel(lookupGroupMap[col], goal[col as keyof GoalData] as string)
                    : ['goalTarget', 'goalCompletionTentativeAmount'].includes(col)
                    ? <span className="text-purple-600 font-medium">{formatCurrency(goal[col as keyof GoalData] as string)}</span>
                    : (goal[col as keyof GoalData] as string) || 'N/A'
                  }
                </td>
              ))}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button onClick={() => handleEdit(goal)} className="text-purple-600 hover:text-purple-800 p-1" title="Edit"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(goal.id)} className="text-red-600 hover:text-red-800 p-1" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GoalsTable;